/**
 * Onboarding wizard (T025) — US1 spine: Notion search/prefill → Client → Project → Sources.
 * Drives the `onboard` orchestrator (T027). Server callables enforce RBAC; this page surfaces a
 * read-only banner for users without an edit role (FR-019).
 */
import { useState } from 'react';
import type { SessionUser } from '../services/auth';
import { SourceLinks } from '../components/SourceLinks';
import {
  notionPrefill,
  onboard,
  OnboardError,
  type NotionMatch,
  type OnboardProgress,
  type OnboardResult,
  type SourceLinkInput,
  type BillingIdentityInput,
} from '../services/onboarding';
import { searchClients } from '../services/clients';
import type { Client, BillingSystem } from '../types/entities';

export function Onboard({ user }: { user: SessionUser }) {
  const readOnly = user.role === null;

  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<NotionMatch[]>([]);
  const [existing, setExisting] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [notionStatus, setNotionStatus] = useState<string | null>(null);
  const [searchBusy, setSearchBusy] = useState(false);

  const [clientName, setClientName] = useState('');
  const [notionCompanyId, setNotionCompanyId] = useState<string | null>(null);
  const [country, setCountry] = useState('');

  const [projectName, setProjectName] = useState('');
  const [pmTool, setPmTool] = useState('');
  const [projectType, setProjectType] = useState('project');

  const [sources, setSources] = useState<SourceLinkInput[]>([]);

  const [billingIdentities, setBillingIdentities] = useState<BillingIdentityInput[]>([]);
  const [biSystem, setBiSystem] = useState<BillingSystem>('holded');
  const [biExternalId, setBiExternalId] = useState('');
  const [biCountry, setBiCountry] = useState('');
  const [biFiscalId, setBiFiscalId] = useState('');

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<OnboardResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<OnboardProgress | null>(null);

  const search = async () => {
    setSearchBusy(true);
    setNotionStatus('Searching…');
    // Existing control-plane clients (a LINK target) — independent of Notion availability.
    try {
      setExisting(await searchClients(query));
    } catch {
      setExisting([]);
    }
    try {
      const { matches } = await notionPrefill({ query, kind: 'company' });
      setMatches(matches);
      setNotionStatus(matches.length ? null : 'No Notion matches — continue manually.');
    } catch {
      // Graceful degradation (FR-002): onboarding proceeds manually.
      setMatches([]);
      setNotionStatus('Notion unavailable — continue manually.');
    } finally {
      setSearchBusy(false);
    }
  };

  /** Notion prefill = a NEW client (clear any existing-client link). Fills the HQ country when Notion
   *  provides one (still editable). */
  const applyMatch = (m: NotionMatch) => {
    setClientName(m.name || m.company || clientName);
    setNotionCompanyId(m.notion_id);
    if (m.country) setCountry(m.country);
    setSelectedClientId(null);
  };

  /** Link the project to an EXISTING client (ADR-0001: add a project/entity to the same company,
   *  not a duplicate). Its HQ country + Notion link come from the existing record. */
  const useExisting = (c: Client) => {
    setSelectedClientId(c.canonical_client_id);
    setClientName(c.canonical_name ?? '');
    setCountry(c.country ?? '');
    setNotionCompanyId(c.notion_company_id ?? null);
  };

  /** Stage a billing entity (one per legal entity/country — ADR-0001) to create during onboard. */
  const addBillingIdentity = () => {
    if (!biExternalId.trim() || !/^[A-Z]{2}$/.test(biCountry)) return;
    setBillingIdentities([
      ...billingIdentities,
      { system: biSystem, external_id: biExternalId.trim(), billing_country: biCountry, fiscal_id: biFiscalId.trim() || null },
    ]);
    setBiExternalId('');
    setBiCountry('');
    setBiFiscalId('');
  };

  const submit = async () => {
    if (country && !/^[A-Z]{2}$/.test(country)) {
      setError('Country must be a 2-letter ISO code (e.g. MX, ES).');
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await onboard(
        {
          client: {
            // Link to the selected existing client, or (on retry) reuse the created id — never create
            // a second client (CC-NNN) for a company that already exists.
            ...(selectedClientId
              ? { canonical_client_id: selectedClientId }
              : progress?.client
                ? { canonical_client_id: progress.client.canonical_client_id }
                : {}),
            canonical_name: clientName,
            notion_company_id: notionCompanyId,
            country,
            is_client: true,
          },
          project: { name: projectName, pm_tool: pmTool, type: projectType },
          sources,
          billingIdentities,
        },
        progress ?? undefined,
      );
      setResult(res);
      setProgress(null);
    } catch (e) {
      if (e instanceof OnboardError) {
        setProgress(e.progress); // keep what succeeded so the next click resumes
        setError(e.message);
      } else {
        setError(e instanceof Error ? e.message : 'Onboarding failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h1>Onboard a project</h1>
      {readOnly && (
        <p style={banner}>You have no edit role — this view is read-only (FR-019).</p>
      )}

      <h2>1 · Prefill from Notion</h2>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          placeholder="Search company (e.g. Arguilea)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1 }}
        />
        <button type="button" onClick={() => void search()} disabled={!query || searchBusy}>
          Search
        </button>
      </div>
      {notionStatus && <p style={{ color: '#888' }}>{notionStatus}</p>}
      <ul>
        {matches.map((m) => (
          <li key={m.notion_id}>
            {m.name} {m.company ? `(${m.company})` : ''}{' '}
            <button type="button" onClick={() => applyMatch(m)}>
              use
            </button>
          </li>
        ))}
      </ul>

      {existing.length > 0 && (
        <div style={{ margin: '8px 0' }}>
          <p style={{ color: '#888', margin: '4px 0' }}>
            Existing clients — <strong>link</strong> the project instead of creating a duplicate:
          </p>
          <ul>
            {existing.map((c) => (
              <li key={c.canonical_client_id}>
                <code>{c.canonical_client_id}</code> {c.canonical_name}{' '}
                {c.country ? `(${c.country})` : ''}{' '}
                <button type="button" onClick={() => useExisting(c)}>
                  use existing
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <h2>2 · Client</h2>
      {selectedClientId && (
        <p style={{ color: '#2a7' }}>
          Linking to existing client <code>{selectedClientId}</code> — the project is added to it, no new
          client is created. Add a per-entity BillingIdentity separately (ADR-0001).{' '}
          <button type="button" onClick={() => setSelectedClientId(null)}>
            create new instead
          </button>
        </p>
      )}
      <Field label="Canonical name">
        <input value={clientName} onChange={(e) => setClientName(e.target.value)} />
      </Field>
      <Field label="Country (ISO-2)">
        <input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} maxLength={2} />
      </Field>
      {notionCompanyId && (
        <p style={{ color: '#888' }}>
          Linked Notion company: <code>{notionCompanyId}</code> (reference only, Art. II)
        </p>
      )}

      <h2>3 · Project</h2>
      <Field label="Project name">
        <input value={projectName} onChange={(e) => setProjectName(e.target.value)} />
      </Field>
      <Field label="PM tool (FR-015)">
        <input value={pmTool} onChange={(e) => setPmTool(e.target.value)} placeholder="jira / clickup / …" />
      </Field>
      <Field label="Type">
        <select value={projectType} onChange={(e) => setProjectType(e.target.value)}>
          <option value="project">project</option>
          <option value="retainer">retainer</option>
        </select>
      </Field>

      <h2>4 · Sources</h2>
      <SourceLinks value={sources} onChange={setSources} disabled={readOnly} />

      <h2>5 · Billing identities</h2>
      <p style={{ color: '#888', margin: '4px 0' }}>
        One per legal entity / country the client bills from (ADR-0001). Optional — can also be added
        later on the Billing identities screen.
      </p>
      <fieldset disabled={readOnly} style={{ border: '1px solid #ddd', padding: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={biSystem} onChange={(e) => setBiSystem(e.target.value as BillingSystem)}>
            <option value="holded">holded (ES)</option>
            <option value="alegra">alegra (MX)</option>
          </select>
          <input placeholder="external_id (VAT / RFC / UUID)" value={biExternalId} onChange={(e) => setBiExternalId(e.target.value)} />
          <input placeholder="billing country (ISO-2)" value={biCountry} onChange={(e) => setBiCountry(e.target.value.toUpperCase())} maxLength={2} style={{ width: 70 }} />
          <input placeholder="fiscal_id (optional)" value={biFiscalId} onChange={(e) => setBiFiscalId(e.target.value)} />
          <button type="button" onClick={addBillingIdentity} disabled={!biExternalId.trim() || !/^[A-Z]{2}$/.test(biCountry)}>
            Add billing identity
          </button>
        </div>
        <ul>
          {billingIdentities.map((b, i) => (
            <li key={`${b.system}-${b.external_id}-${i}`}>
              <code>{b.system}</code> · {b.external_id} · {b.billing_country}
              {b.fiscal_id ? ` · ${b.fiscal_id}` : ''}{' '}
              <button type="button" onClick={() => setBillingIdentities(billingIdentities.filter((_, j) => j !== i))}>
                remove
              </button>
            </li>
          ))}
        </ul>
      </fieldset>

      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={readOnly || busy || !clientName || !projectName || !pmTool}
        >
          {busy ? 'Onboarding…' : 'Create project'}
        </button>
      </div>

      {error && <p style={{ color: 'crimson' }}>Error: {error}</p>}
      {progress && !result && (
        <p style={{ color: '#a60' }}>
          Partial progress saved (client {progress.client?.canonical_client_id ?? '—'}, project{' '}
          {progress.project?.project_id ?? '—'}). Click "Create project" again to resume — it will not
          create a duplicate client.
        </p>
      )}
      {result && (
        <div style={{ marginTop: 16, padding: 12, background: '#f3fbf3', border: '1px solid #bcdfbc' }}>
          <strong>Onboarded.</strong>
          <ul>
            <li>Client: {result.client.canonical_client_id}</li>
            <li>Project: {result.project.project_id}</li>
            <li>Sources: {result.sources.length}</li>
            <li>Billing identities: {result.billingIdentities.length}</li>
          </ul>
          {result.warnings.length > 0 && (
            <p style={{ color: '#a60' }}>Warnings: {result.warnings.join('; ')}</p>
          )}
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0' }}>
      <span style={{ width: 180 }}>{label}</span>
      {children}
    </label>
  );
}

const banner: React.CSSProperties = {
  background: '#fff7e6',
  border: '1px solid #ffd591',
  padding: 8,
  borderRadius: 4,
};
